import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui-kit/button';
import {
  useAccountActivation,
  useValidateActivationCodeMutation,
} from '@/modules/auth/hooks/use-auth';
import { LOGIN_COLORS } from '@/modules/auth/constants/login-theme';
import { useAuthState } from '@/state/client-middleware';
import { useToast } from '@/hooks/use-toast';

export const AccountActivationPage = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code') ?? '';
  const { isMounted, isAuthenticated } = useAuthState();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { mutateAsync: validateActivation, isPending } = useValidateActivationCodeMutation();
  const { mutateAsync: activate, isPending: isActivating } = useAccountActivation();
  const [isValidCode, setIsValidCode] = useState<boolean | null>(null);

  // No password is set: the person signs in with SSO once the account is active.
  const handleActivate = async () => {
    try {
      await activate({ code });
      navigate('/success');
    } catch {
      toast({
        variant: 'destructive',
        title: t('ERROR'),
        description: t('PLEASE_TRY_AGAIN'),
      });
    }
  };

  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!code || isAuthenticated) return;

    const validate = async () => {
      try {
        const res = await validateActivation({
          activationCode: code,
          projectKey: import.meta.env.VITE_X_BLOCKS_KEY || '',
        });

        if (res.isSuccess) {
          setIsValidCode(true);
        } else if (res.userId) {
          // Code expired/invalid but we have userId — go to verification-failed so user can resend
          navigate(`/activate-failed?userId=${res.userId}`);
        } else if (res.errors) {
          // No userId available — redirect to login with error toast
          toast({
            variant: 'destructive',
            title: t('ERROR'),
            description: `${t('ACTIVATION_EXPIRED')}. ${t('PLEASE_TRY_AGAIN')}`,
          });
          navigate('/login');
        } else {
          toast({
            variant: 'destructive',
            title: t('ERROR'),
            description: `${t('ACTIVATION_EXPIRED')}. ${t('PLEASE_TRY_AGAIN')}`,
          });
          navigate('/login');
        }
      } catch (error) {
        // Unexpected error — redirect to login with error toast
        toast({
          variant: 'destructive',
          title: t('ERROR'),
          description: `${t('ACTIVATION_EXPIRED')}. ${t('PLEASE_TRY_AGAIN')}`,
        });
        navigate('/login');
      }
    };

    validate();
  }, [code, isAuthenticated, navigate, validateActivation, toast, t]);

  if (!isMounted || isPending || (code && isValidCode === null)) return null;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-1.5">
        <h2
          className="break-words text-[2rem] font-bold leading-tight tracking-tight sm:text-[2.125rem]"
          style={{ color: LOGIN_COLORS.textPrimary }}
        >
          {t('ACTIVATE_YOUR_ACCOUNT')}
        </h2>
        <p className="text-sm font-normal leading-5" style={{ color: LOGIN_COLORS.textMuted }}>
          {t('ACTIVATE_ACCOUNT_SSO_DESCRIPTION')}
        </p>
        <div className="flex flex-wrap items-center gap-x-1">
          <span className="text-sm font-normal leading-5" style={{ color: LOGIN_COLORS.textMuted }}>
            {t('ALREADY_HAVE_ACCOUNT')}
          </span>
          <Link
            to="/login"
            className="text-sm font-semibold leading-5 hover:underline"
            style={{ color: LOGIN_COLORS.primary }}
          >
            {t('LOG_IN')}
          </Link>
        </div>
      </div>
      <Button
        className="h-12 w-full rounded-xl border-0 text-sm font-semibold shadow-none hover:opacity-90"
        size="lg"
        onClick={handleActivate}
        loading={isActivating}
        disabled={!code || isActivating}
        style={{ backgroundColor: LOGIN_COLORS.primary, color: LOGIN_COLORS.pureWhite }}
      >
        {t('ACTIVATE')}
      </Button>
    </div>
  );
};
